import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import type {
  Location,
  Zone,
  BinType,
  LocationStatus,
  CreateLocationInput,
  UpdateLocationInput,
} from '../../types';

const binTypes: { value: BinType; label: string }[] = [
  { value: 'standard', label: 'Standard' },
  { value: 'bulk', label: 'Bulk' },
  { value: 'calibration', label: 'Calibration' },
  { value: 'staging', label: 'Staging' },
  { value: 'returns', label: 'Returns' },
  { value: 'overflow', label: 'Overflow' },
];

const locationStatuses: { value: LocationStatus; label: string }[] = [
  { value: 'active', label: 'Active' },
  { value: 'full', label: 'Full' },
  { value: 'reserved', label: 'Reserved' },
  { value: 'maintenance', label: 'Maintenance' },
  { value: 'inactive', label: 'Inactive' },
];

interface LocationFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  location?: Location | null;
  zones: Zone[];
  defaultZoneId?: string;
  onSubmit: (data: CreateLocationInput | UpdateLocationInput) => Promise<void>;
  onDelete?: () => void;
  isLoading?: boolean;
}

export function LocationFormModal({
  open,
  onOpenChange,
  location,
  zones,
  defaultZoneId,
  onSubmit,
  onDelete,
  isLoading,
}: LocationFormModalProps) {
  const isEditing = !!location;

  const [formData, setFormData] = useState<CreateLocationInput>({
    zone_id: defaultZoneId || '',
    bin_code: '',
    name: '',
    bin_type: 'standard',
    status: 'active',
    capacity_units: undefined,
    notes: '',
  });

  useEffect(() => {
    if (location) {
      setFormData({
        zone_id: location.zone_id,
        bin_code: location.bin_code,
        name: location.name || '',
        bin_type: location.bin_type,
        status: location.status,
        capacity_units: location.capacity_units,
        notes: location.notes || '',
      });
    } else {
      setFormData({
        zone_id: defaultZoneId || zones[0]?.id || '',
        bin_code: '',
        name: '',
        bin_type: 'standard',
        status: 'active',
        capacity_units: undefined,
        notes: '',
      });
    }
  }, [location, open, defaultZoneId, zones]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (isEditing) {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { zone_id, ...updateData } = formData;
        await onSubmit(updateData);
        toast.success('Location updated successfully');
      } else {
        await onSubmit(formData);
        toast.success('Location created successfully');
      }
      onOpenChange(false);
    } catch (error: any) {
      const message = error?.response?.data?.message || error?.message || 'Unknown error occurred';
      toast.error(`Failed to ${isEditing ? 'update' : 'create'} location: ${message}`);
      console.error(`Error ${isEditing ? 'updating' : 'creating'} location:`, error);
      // Keep form open for retry
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Edit Location' : 'Create Location'}</DialogTitle>
          <DialogDescription>
            {isEditing
              ? 'Update the bin location details below.'
              : 'Add a new bin location within a zone.'}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="zone_id">Zone</Label>
                <Select
                  value={formData.zone_id}
                  onValueChange={(v) => setFormData({ ...formData, zone_id: v })}
                  disabled={isEditing}
                >
                  <SelectTrigger id="zone_id">
                    <SelectValue placeholder="Select zone" />
                  </SelectTrigger>
                  <SelectContent>
                    {zones.map((zone) => (
                      <SelectItem key={zone.id} value={zone.id}>
                        <div className="flex items-center gap-2">
                          <div
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: zone.color || '#6b7280' }}
                          />
                          {zone.code} - {zone.name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="bin_code">Bin Code</Label>
                <Input
                  id="bin_code"
                  placeholder="e.g., A1, B3, F1-1"
                  value={formData.bin_code}
                  onChange={(e) => setFormData({ ...formData, bin_code: e.target.value.toUpperCase() })}
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="name">Name (optional)</Label>
              <Input
                id="name"
                placeholder="e.g., Small parts bin, Bulk storage"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="bin_type">Bin Type</Label>
                <Select
                  value={formData.bin_type}
                  onValueChange={(v) => setFormData({ ...formData, bin_type: v as BinType })}
                >
                  <SelectTrigger id="bin_type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {binTypes.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="status">Status</Label>
                <Select
                  value={formData.status}
                  onValueChange={(v) => setFormData({ ...formData, status: v as LocationStatus })}
                >
                  <SelectTrigger id="status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {locationStatuses.map((status) => (
                      <SelectItem key={status.value} value={status.value}>
                        {status.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="capacity_units">Capacity Units (optional)</Label>
              <Input
                id="capacity_units"
                type="number"
                placeholder="e.g., 100"
                value={formData.capacity_units || ''}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    capacity_units: e.target.value ? parseInt(e.target.value) : undefined,
                  })
                }
                min={0}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Notes (optional)</Label>
              <Textarea
                id="notes"
                placeholder="Additional notes about this location..."
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={2}
              />
            </div>
          </div>

          <DialogFooter className="flex justify-between sm:justify-between">
            {isEditing && onDelete ? (
              <Button type="button" variant="destructive" onClick={onDelete}>
                Delete
              </Button>
            ) : (
              <div />
            )}
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? 'Saving...' : isEditing ? 'Update Location' : 'Create Location'}
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
